import { Observable } from "rxjs";
import { map, mapTo } from "rxjs/operators";
import { inject, injectable } from "tsyringe";

import { GenericSuccessResponse } from "src/infra/http/types";
import { autoBind } from "src/shared/decorators/autobind";
import { HttpClient } from "src/infra/http/client";
import { Environment } from "src/libs/environment";
import { replaceString } from "src/libs/replace-string";
import { NotificationsEndpoints } from "src/containers/configuration/constants/endpoints";
import { PushSubscription } from "src/containers/configuration/models/push-subcription";
import { PushSubscriptionRequest } from "src/containers/configuration/models/push-subscription-request";

const getBusinessProcess = (method = "GET"): string => {
    if (method === "GET") {
        return "ms-ca-mep-find-notification-subscription";
    }
    if (method === "POST") {
        return "ms-ca-mep-enroll-notification-subscription";
    }
    if (method === "PUT") {
        return "ms-ca-mep-update-notification-subscription";
    }
    return "";
};

@injectable()
export class NotificationsRepository {
    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    getSubscription(
        userIdentity: string
    ): Observable<Array<Record<string, any>>> {
        return this.http.getJSON<Array<Record<string, any>>>(
            NotificationsEndpoints.GetSubscription,
            {
                host: Environment.string("APIMGMT_URL"),
                isPublicRequest: false,
                customHeader: {
                    ...this.http.httpUtils.getBaseHeaders(),
                    ...this.http.httpUtils.getMsBaseHeaders(
                        userIdentity,
                        getBusinessProcess("GET")
                    ),
                    "Ocp-Apim-Trace": true,
                },
            }
        );
    }

    @autoBind
    createSubscription(
        request: PushSubscriptionRequest,
        userIdentity: string
    ): Observable<PushSubscriptionRequest> {
        return this.http
            .post<Record<string, any>>(
                NotificationsEndpoints.CreateSubscription,
                {
                    host: Environment.string("APIMGMT_URL"),
                    isPublicRequest: false,
                    customHeader: {
                        ...this.http.httpUtils.getBaseHeaders(),
                        ...this.http.httpUtils.getMsBaseHeaders(
                            userIdentity,
                            getBusinessProcess("POST")
                        ),
                        "Ocp-Apim-Trace": true,
                    },
                    data: request.toJson(),
                }
            )
            .pipe(
                map(() => {
                    return PushSubscription.fromJson({
                        ...request.toJson(),
                        enabled: true,
                    });
                })
            );
    }

    @autoBind
    updateSubscription(
        subscription: PushSubscriptionRequest,
        subscriptionId: number,
        userIdentity: string
    ): Observable<GenericSuccessResponse> {
        const url = replaceString(NotificationsEndpoints.UpdateSubscription, {
            ":subscriptionId": subscriptionId,
        });
        return this.http
            .put<Record<string, any>>(url, {
                host: Environment.string("APIMGMT_URL"),
                isPublicRequest: false,
                customHeader: {
                    ...this.http.httpUtils.getBaseHeaders(),
                    ...this.http.httpUtils.getMsBaseHeaders(
                        userIdentity,
                        getBusinessProcess("PUT")
                    ),
                    "Ocp-Apim-Trace": true,
                },
                data: subscription.toJson(),
            })
            .pipe(mapTo({ status: "ok", code: 200 }));
    }
}
